<script lang="ts">
	import { _ } from 'svelte-i18n';
	import Header from '$lib/components/sections/Header.svelte';
	import Footer from '$lib/components/sections/Footer.svelte';
	import Button from '$lib/components/primitives/Button.svelte';
	import Icon from '$lib/components/primitives/Icon.svelte';
	import TeamAvatar from '$lib/components/team/TeamAvatar.svelte';

	const teamMembers = $derived([
		{
			name: 'Robert',
			role: 'team.roles.founder',
			avatar: 'robert' as const,
			linkedin: 'https://www.linkedin.com/in/robertblixt'
		},
		{
			name: 'Mahder',
			role: 'team.roles.developer',
			avatar: 'mahder' as const
		}
	]);
</script>

<svelte:head>
	<title>{$_('team.meta.title')}</title>
	<meta name="description" content={$_('team.meta.description')} />
	<meta property="og:title" content="{$_('team.meta.title')}" />
	<meta property="og:description" content={$_('team.meta.description')} />
</svelte:head>

<Header />

<div class="team-page">
	<!-- Hero Section with Story -->
	<section class="hero">
		<div class="container">
			<div class="hero__content">
				<h1 class="hero__title">
					{$_('team.hero.title')}
				</h1>
				<p class="hero__subtitle">
					{$_('team.hero.subtitle')}
				</p>

				<div class="hero__story">
					<h2 class="story__title">{$_('team.story.title')}</h2>
					<div class="story__text">
						<p class="story__paragraph">{$_('team.story.paragraph1')}</p>
						<p class="story__paragraph">{$_('team.story.paragraph2')}</p>
						<p class="story__paragraph">{$_('team.story.paragraph3')}</p>
					</div>
					<div class="story__stats">
						<div class="stat">
							<div class="stat__number">2021</div>
							<div class="stat__label">{$_('team.story.stats.founded')}</div>
						</div>
						<div class="stat">
							<div class="stat__number">{teamMembers.length}</div>
							<div class="stat__label">{$_('team.story.stats.teammates')}</div>
						</div>
						<div class="stat">
							<div class="stat__number">∞</div>
							<div class="stat__label">{$_('team.story.stats.passion')}</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>

	<!-- Team Members Section -->
	<section class="members">
		<div class="container">
			<div class="members__header">
				<h2 class="members__title">{$_('team.members.title')}</h2>
				<p class="members__subtitle">{$_('team.members.subtitle')}</p>
			</div>

			<div class="members__grid">
				{#each teamMembers as member}
					<div class="member-card">
						<div class="member-card__image">
							<TeamAvatar member={member.avatar} size={100} />
						</div>
						<div class="member-card__content">
							<h3 class="member-card__name">{member.name}</h3>
							<p class="member-card__role">{$_(member.role)}</p>
							{#if member.linkedin}
								<a
									href={member.linkedin}
									target="_blank"
									rel="noopener noreferrer"
									class="member-card__linkedin"
									aria-label="View {member.name}'s LinkedIn profile"
								>
									<Icon name="linkedin" size={20} />
								</a>
							{/if}
						</div>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- Our Mission Section -->
	<section class="mission">
		<div class="container">
			<div class="mission__content">
				<h2 class="mission__title">{$_('team.mission.title')}</h2>
				<p class="mission__lead">{$_('team.mission.lead')}</p>
				<div class="mission__grid">
					<div class="mission__card">
						<Icon name="rocket" size={32} />
						<h3>{$_('team.mission.entrepreneurFirst.title')}</h3>
						<p>{$_('team.mission.entrepreneurFirst.description')}</p>
					</div>
					<div class="mission__card">
						<Icon name="target" size={32} />
						<h3>{$_('team.mission.focusedSolutions.title')}</h3>
						<p>{$_('team.mission.focusedSolutions.description')}</p>
					</div>
					<div class="mission__card">
						<Icon name="users" size={32} />
						<h3>{$_('team.mission.userObsessed.title')}</h3>
						<p>{$_('team.mission.userObsessed.description')}</p>
					</div>
				</div>
			</div>
		</div>
	</section>

	<!-- Culture Section -->
	<section class="culture">
		<div class="container">
			<div class="culture__grid">
				<div class="culture__content">
					<h2 class="culture__title">{$_('team.culture.title')}</h2>
					<p class="culture__paragraph">{$_('team.culture.paragraph1')}</p>
					<p class="culture__paragraph">{$_('team.culture.paragraph2')}</p>
					<p class="culture__paragraph">{$_('team.culture.paragraph3')}</p>
				</div>
				<div class="culture__benefits">
					<div class="benefit-card">
						<Icon name="globe" size={32} />
						<h3 class="benefit-card__title">{$_('team.culture.benefits.remote.title')}</h3>
						<p class="benefit-card__description">
							{$_('team.culture.benefits.remote.description')}
						</p>
					</div>
					<div class="benefit-card">
						<Icon name="palmtree" size={32} />
						<h3 class="benefit-card__title">{$_('team.culture.benefits.flexible.title')}</h3>
						<p class="benefit-card__description">
							{$_('team.culture.benefits.flexible.description')}
						</p>
					</div>
					<div class="benefit-card">
						<Icon name="target" size={32} />
						<h3 class="benefit-card__title">{$_('team.culture.benefits.valuedriven.title')}</h3>
						<p class="benefit-card__description">
							{$_('team.culture.benefits.valuedriven.description')}
						</p>
					</div>
				</div>
			</div>
		</div>
	</section>
</div>

<Footer />

<style>
	.team-page {
		min-height: 100vh;
		padding-top: var(--space-4);
	}

	/* Override Tailwind's container with proper constraints */
	:global(.container) {
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 1rem;
		width: 100%;
	}

	/* Hero Section */
	.hero {
		padding: var(--space-12) 0 var(--space-16);
		background: linear-gradient(180deg, var(--bg-secondary) 0%, transparent 100%);
		text-align: center;
	}

	.hero__content {
		max-width: 900px;
		margin: 0 auto;
	}

	.hero__title {
		font-size: clamp(3rem, 6vw, 4.5rem);
		font-weight: var(--font-weight-bold);
		line-height: 1.1;
		color: var(--text-primary);
		margin-bottom: var(--space-6);
		letter-spacing: -0.03em;
	}

	.hero__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		line-height: 1.6;
		max-width: 600px;
		margin: 0 auto var(--space-12);
	}

	/* Story within Hero */
	.hero__story {
		margin-top: var(--space-8);
	}

	.story__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-bold);
		color: var(--text-primary);
		margin-bottom: var(--space-6);
	}

	.story__text {
		margin-bottom: var(--space-10);
	}

	.story__paragraph {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		line-height: 1.8;
		margin-bottom: var(--space-4);
		max-width: 700px;
		margin-left: auto;
		margin-right: auto;
	}

	.story__paragraph:last-child {
		margin-bottom: 0;
	}

	.story__stats {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: var(--space-8);
		max-width: 600px;
		margin: 0 auto;
	}

	.stat {
		text-align: center;
	}

	.stat__number {
		font-size: var(--font-size-4xl);
		font-weight: var(--font-weight-bold);
		color: var(--accent-primary);
		margin-bottom: var(--space-2);
		letter-spacing: -0.02em;
	}

	:global(.dark) .stat__number {
		color: var(--text-brand);
	}

	.stat__label {
		font-size: var(--font-size-sm);
		color: var(--text-tertiary);
		text-transform: uppercase;
		letter-spacing: 0.08em;
		font-weight: var(--font-weight-medium);
	}

	/* Members Section */
	.members {
		padding: var(--space-16) 0 var(--space-12);
	}

	.members__header {
		text-align: center;
		margin-bottom: var(--space-6);
	}

	.members__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-bold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}

	.members__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		max-width: 600px;
		margin: 0 auto;
	}

	.members__grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(280px, 340px));
		justify-content: center;
		gap: var(--space-6);
	}

	.member-card {
		background: var(--bg-primary);
		border-radius: var(--radius-md);
		overflow: hidden;
		transition:
			transform 0.15s ease,
			box-shadow 0.15s ease;
		border: 1px solid var(--border-light);
	}

	.member-card:hover {
		transform: translateY(-2px);
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
	}

	:global(.dark) .member-card {
		border-color: var(--border-default);
		background: var(--bg-elevated);
	}

	:global(.dark) .member-card:hover {
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
	}

	.member-card__image {
		height: 160px;
		background: var(--bg-secondary);
		display: flex;
		align-items: center;
		justify-content: center;
		padding: var(--space-6);
		position: relative;
	}

	.member-card__image::after {
		content: '';
		position: absolute;
		bottom: 0;
		left: var(--space-6);
		right: var(--space-6);
		height: 1px;
		background: var(--border-light);
		opacity: 0.5;
	}

	:global(.dark) .member-card__image {
		background: var(--bg-tertiary);
	}

	.member-card__content {
		padding: var(--space-5);
	}

	.member-card__name {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-medium);
		color: var(--text-primary);
		margin-bottom: 2px;
	}

	.member-card__role {
		font-size: 12px;
		color: var(--text-tertiary);
		font-weight: var(--font-weight-normal);
		text-transform: uppercase;
		letter-spacing: 0.08em;
		margin-bottom: var(--space-3);
		opacity: 0.7;
	}

	.member-card__linkedin {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 32px;
		height: 32px;
		color: var(--text-tertiary);
		text-decoration: none;
		transition: all 0.2s ease;
		border-radius: var(--radius-sm);
		background: transparent;
		opacity: 0.6;
	}

	.member-card__linkedin:hover {
		color: var(--text-secondary);
		background: var(--bg-secondary);
		transform: translateY(-2px);
		opacity: 1;
	}

	:global(.dark) .member-card__linkedin {
		color: var(--text-tertiary);
	}

	:global(.dark) .member-card__linkedin:hover {
		color: var(--text-secondary);
		background: var(--bg-tertiary);
	}

	/* Mission Section */
	.mission {
		padding: var(--space-8) 0;
		background: var(--bg-secondary);
	}

	.mission__content {
		text-align: center;
		max-width: 900px;
		margin: 0 auto;
	}

	.mission__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-bold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}

	.mission__lead {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		line-height: 1.6;
		margin-bottom: var(--space-8);
	}

	.mission__grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: var(--space-8);
		margin-top: var(--space-8);
	}

	.mission__card {
		text-align: center;
		padding: var(--space-6);
		background: var(--bg-primary);
		border-radius: var(--radius-lg);
		border: 1px solid var(--border-light);
		transition:
			transform 0.2s ease,
			box-shadow 0.2s ease;
	}

	.mission__card:hover {
		transform: translateY(-4px);
		box-shadow: var(--shadow-lg);
	}

	.mission__card :global(svg) {
		color: var(--brand-primary);
		margin: 0 auto var(--space-4);
	}

	.mission__card h3 {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-3);
	}

	.mission__card p {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		line-height: 1.6;
	}

	/* Culture Section */
	.culture {
		padding: var(--space-8) 0;
		background: var(--bg-primary);
	}

	.culture__grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--space-12);
		align-items: center;
	}

	.culture__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-bold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}

	.culture__paragraph {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		line-height: 1.6;
		margin-bottom: var(--space-3);
	}

	.culture__paragraph:last-child {
		margin-bottom: 0;
	}

	.culture__benefits {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: var(--space-4);
	}

	.benefit-card {
		padding: var(--space-6);
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-xl);
	}

	:global(.dark) .benefit-card {
		background: var(--bg-tertiary);
		border-color: var(--border-default);
	}

	.benefit-card :global(svg) {
		color: var(--brand-primary);
		margin-bottom: var(--space-2);
	}

	:global(.dark) .benefit-card :global(svg) {
		color: var(--text-brand);
	}

	.benefit-card__title {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-2);
	}

	.benefit-card__description {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		line-height: 1.5;
	}

	/* Responsive Design */
	@media (max-width: 968px) {
		.mission__grid {
			grid-template-columns: 1fr;
			gap: var(--space-4);
		}

		.culture__grid {
			grid-template-columns: 1fr;
			gap: var(--space-8);
		}

		.culture__benefits {
			grid-template-columns: 1fr;
		}
	}

	@media (max-width: 768px) {
		.hero__title {
			font-size: 2.5rem;
		}

		.story__stats {
			grid-template-columns: 1fr;
			gap: var(--space-6);
		}

		.members__grid {
			grid-template-columns: 1fr;
		}
	}
</style>
